// src/components/MagicImage.tsx
import { forwardRef as forwardRef2, useMemo as useMemo5, useState as useState7, useEffect as useEffect5 } from "react";

// src/components/MagicImageMin.tsx
import { forwardRef, useCallback as useCallback3, useEffect as useEffect4, useMemo as useMemo3, useRef as useRef4, useState as useState5 } from "react";

// src/context/Provider.tsx
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var MediaMagicContext = createContext(null);
function MediaMagicProvider({ config, children }) {
  const value = useMemo(
    () => ({
      ...config,
      isConfigured: true
    }),
    [config]
  );
  return /* @__PURE__ */ jsx(MediaMagicContext.Provider, { value, children });
}
function useMediaMagicConfig() {
  const context = useContext(MediaMagicContext);
  if (!context) {
    throw new Error(
      "useMediaMagicConfig must be used within a MediaMagicProvider. Wrap your app with <MediaMagicProvider config={...}>."
    );
  }
  return context;
}
function useOptionalMediaMagicConfig() {
  return useContext(MediaMagicContext);
}

// src/hooks/useStatsOverlay.ts
import { useContext as useContext3 } from "react";

// src/components/StatsProvider.tsx
import {
  createContext as createContext2,
  useCallback,
  useEffect as useEffect2,
  useMemo as useMemo2,
  useRef as useRef2,
  useState as useState2
} from "react";

// src/utils/stats.ts
function formatBytes(bytes, decimals = 2) {
  if (bytes === void 0 || bytes === null || bytes < 0) {
    return "\u2014";
  }
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}
function calculateReductionPercent(original, optimized) {
  if (original === void 0 || optimized === void 0 || original <= 0 || optimized < 0) {
    return void 0;
  }
  const percent = (original - optimized) / original * 100;
  return Math.max(0, Math.min(100, percent));
}
function calculateAggregates(statsMap) {
  const stats = Array.from(statsMap.values());
  const totalImages = stats.length;
  if (totalImages === 0) {
    return {
      totalImages: 0,
      totalOriginalBytes: 0,
      totalOptimizedBytes: 0,
      totalReductionPercent: 0,
      totalLoadTimeMs: 0,
      cacheHitRatio: 0,
      avgLoadTimeMs: 0,
      avgReductionPercent: 0
    };
  }
  let totalOriginalBytes = 0;
  let totalOptimizedBytes = 0;
  let totalLoadTimeMs = 0;
  let cacheHits = 0;
  let loadSamples = 0;
  let reductionSamples = 0;
  let totalReductionSum = 0;
  for (const stat of stats) {
    if (stat.originalSizeBytes !== void 0 && stat.originalSizeBytes > 0) {
      totalOriginalBytes += stat.originalSizeBytes;
    }
    if (stat.optimizedSizeBytes !== void 0 && stat.optimizedSizeBytes >= 0) {
      totalOptimizedBytes += stat.optimizedSizeBytes;
    }
    if (stat.loadTimeMs !== void 0 && stat.loadTimeMs > 0) {
      totalLoadTimeMs += stat.loadTimeMs;
      loadSamples += 1;
    }
    if (stat.cacheStatus === "hit") {
      cacheHits += 1;
    }
    if (stat.sizeReductionPercent !== void 0 && stat.sizeReductionPercent >= 0) {
      totalReductionSum += stat.sizeReductionPercent;
      reductionSamples += 1;
    }
  }
  const totalReductionPercent = totalOriginalBytes > 0 ? (totalOriginalBytes - totalOptimizedBytes) / totalOriginalBytes * 100 : reductionSamples > 0 ? totalReductionSum / reductionSamples : 0;
  const cacheHitRatio = totalImages > 0 ? cacheHits / totalImages : 0;
  const avgLoadTimeMs = loadSamples > 0 ? totalLoadTimeMs / loadSamples : 0;
  const avgReductionPercent = reductionSamples > 0 ? totalReductionSum / reductionSamples : 0;
  return {
    totalImages,
    totalOriginalBytes,
    totalOptimizedBytes,
    totalReductionPercent: Math.max(0, Math.min(100, totalReductionPercent)),
    totalLoadTimeMs: Math.round(totalLoadTimeMs),
    cacheHitRatio: Math.max(0, Math.min(1, cacheHitRatio)),
    avgLoadTimeMs: Math.round(avgLoadTimeMs),
    avgReductionPercent: Math.max(0, Math.min(100, avgReductionPercent))
  };
}
function getDefaultKeyBinding() {
  if (typeof navigator === "undefined") {
    return "Ctrl+B";
  }
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0 || navigator.userAgent.toUpperCase().indexOf("MAC") >= 0;
  return isMac ? "Cmd+B" : "Ctrl+B";
}
function getCacheColor(ratio) {
  if (ratio >= 0.9) return "#60a5fa";
  if (ratio >= 0.75) return "#4ade80";
  if (ratio >= 0.5) return "#fb923c";
  return "#f87171";
}
function getLoadTimeColor(ms) {
  if (ms <= 350) return "#60a5fa";
  if (ms <= 1e3) return "#4ade80";
  if (ms <= 2500) return "#fb923c";
  return "#f87171";
}
function getReductionColor(percent) {
  if (percent >= 60) return "#4ade80";
  if (percent >= 30) return "#3EAA48";
  if (percent >= 10) return "#fb923c";
  return "#9ca3af";
}

// src/components/GlobalStatsDashboard.tsx
import { useContext as useContext2, useEffect, useRef, useState } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function GlobalStatsDashboard() {
  const statsContext = useContext2(StatsContext);
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const raw = localStorage.getItem("mm_stats_dashboard_pos");
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
        return { x: parsed.x, y: parsed.y };
      }
    } catch {
      return null;
    }
    return null;
  });
  if (!statsContext) {
    return null;
  }
  const { aggregates } = statsContext;
  const keyBinding = getDefaultKeyBinding();
  useEffect(() => {
    if (!position || typeof window === "undefined") {
      return;
    }
    try {
      localStorage.setItem("mm_stats_dashboard_pos", JSON.stringify(position));
    } catch {
    }
  }, [position]);
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const handleMove = (event) => {
      if (!dragRef.current || !containerRef.current) {
        return;
      }
      const rect = containerRef.current.getBoundingClientRect();
      const nextX = event.clientX - dragRef.current.offsetX;
      const nextY = event.clientY - dragRef.current.offsetY;
      const minX = 8;
      const minY = 8;
      const maxX = Math.max(minX, window.innerWidth - rect.width - 8);
      const maxY = Math.max(minY, window.innerHeight - rect.height - 8);
      setPosition({
        x: Math.min(Math.max(nextX, minX), maxX),
        y: Math.min(Math.max(nextY, minY), maxY)
      });
    };
    const handleUp = () => {
      setIsDragging(false);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [isDragging]);
  const handlePointerDown = (event) => {
    if (event.button !== 0) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };
  const dashboardStyle = {
    position: "fixed",
    top: position ? `${position.y}px` : "76px",
    left: position ? `${position.x}px` : void 0,
    right: position ? void 0 : "16px",
    backgroundColor: "rgba(15, 23, 42, 0.72)",
    color: "#e2e8f0",
    padding: "16px",
    borderRadius: "8px",
    fontSize: "12px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace',
    lineHeight: "1.6",
    zIndex: 200,
    minWidth: "280px",
    maxWidth: "380px",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    backdropFilter: "blur(8px)",
    boxShadow: isDragging ? "0 20px 40px rgba(0, 0, 0, 0.6), 0 8px 16px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)" : "0 12px 24px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
    cursor: "move",
    userSelect: "none",
    transition: isDragging ? "none" : "box-shadow 0.2s ease"
  };
  const titleStyle = {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "12px",
    color: "#f1f5f9",
    borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
    paddingBottom: "8px"
  };
  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    gap: "12px"
  };
  const labelStyle = {
    opacity: 0.7,
    flex: 1
  };
  const valueStyle = {
    fontWeight: 600,
    color: "#4ade80",
    textAlign: "right",
    flex: 1
  };
  const primaryMetricStyle = {
    fontSize: "16px",
    fontWeight: 600,
    color: getReductionColor(aggregates.totalReductionPercent),
    textAlign: "right",
    flex: 1,
    textShadow: "0 0 8px rgba(74, 222, 128, 0.4)",
    letterSpacing: "0.02em"
  };
  const separatorStyle = {
    height: "1px",
    background: "rgba(148, 163, 184, 0.2)",
    margin: "8px 0"
  };
  const footerStyle = {
    marginTop: "12px",
    paddingTop: "8px",
    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
    fontSize: "11px",
    opacity: 0.6,
    textAlign: "center"
  };
  const highlightStyle = {
    color: "#60a5fa"
  };
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, style: dashboardStyle, onPointerDown: handlePointerDown, children: [
    /* @__PURE__ */ jsx2("div", { style: titleStyle, children: "Media Magic Stats" }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Size Reduction" }),
      /* @__PURE__ */ jsxs("span", { style: primaryMetricStyle, children: [
        aggregates.totalReductionPercent.toFixed(1),
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsx2("div", { style: separatorStyle }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Images" }),
      /* @__PURE__ */ jsx2("span", { style: valueStyle, children: aggregates.totalImages })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Original Size" }),
      /* @__PURE__ */ jsx2("span", { style: valueStyle, children: formatBytes(aggregates.totalOriginalBytes) })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Optimized Size" }),
      /* @__PURE__ */ jsx2("span", { style: valueStyle, children: formatBytes(aggregates.totalOptimizedBytes) })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Bytes Saved" }),
      /* @__PURE__ */ jsx2("span", { style: valueStyle, children: formatBytes(aggregates.totalOriginalBytes - aggregates.totalOptimizedBytes) })
    ] }),
    /* @__PURE__ */ jsx2("div", { style: separatorStyle }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Cache Hit Ratio" }),
      /* @__PURE__ */ jsxs("span", { style: { ...valueStyle, color: getCacheColor(aggregates.cacheHitRatio) }, children: [
        (aggregates.cacheHitRatio * 100).toFixed(0),
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Avg Load Time" }),
      /* @__PURE__ */ jsxs("span", { style: { ...valueStyle, color: getLoadTimeColor(aggregates.avgLoadTimeMs) }, children: [
        aggregates.avgLoadTimeMs.toFixed(0),
        " ms"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Total Load Time" }),
      /* @__PURE__ */ jsxs("span", { style: { ...valueStyle, color: getLoadTimeColor(aggregates.totalLoadTimeMs) }, children: [
        aggregates.totalLoadTimeMs,
        " ms"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: rowStyle, children: [
      /* @__PURE__ */ jsx2("span", { style: labelStyle, children: "Avg Reduction" }),
      /* @__PURE__ */ jsxs("span", { style: valueStyle, children: [
        aggregates.avgReductionPercent.toFixed(1),
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: footerStyle, children: [
      "Press ",
      /* @__PURE__ */ jsx2("span", { style: highlightStyle, children: keyBinding }),
      " to close"
    ] })
  ] });
}

// src/components/StatsProvider.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var warn = (..._args) => {
};
var StatsContext = createContext2(null);
function getBasePath() {
  if (typeof import.meta !== "undefined") {
    const env = import.meta.env;
    const envBase = env?.BASE_URL;
    if (envBase && envBase !== "/") {
      return envBase.endsWith("/") ? envBase.slice(0, -1) : envBase;
    }
  }
  return "";
}
function resolveSwPath(swPath, basePath) {
  if (/^https?:\/\//i.test(swPath)) return swPath;
  if (!basePath) return swPath;
  if (swPath.startsWith("/")) {
    if (swPath.startsWith(`${basePath}/`)) return swPath;
    return `${basePath}${swPath}`;
  }
  return `${basePath}/${swPath}`;
}
function resolveSwScope(basePath) {
  return basePath ? `${basePath}/` : "/";
}
function normalizeStatsUrl(url) {
  try {
    const u = new URL(url);
    u.searchParams.delete("__mmr");
    return u.toString();
  } catch {
    return url.replace(/[?&]__mmr=\d+/, "");
  }
}
function normalizeCdnPattern(rawUrl) {
  const value = rawUrl.trim();
  if (!value) return void 0;
  try {
    return new URL(value).hostname;
  } catch {
    if (value.startsWith("//")) {
      try {
        return new URL(`https:${value}`).hostname;
      } catch {
        return value;
      }
    }
    return value;
  }
}
function StatsProvider({
  children,
  cdnPattern,
  swPath = typeof globalThis !== "undefined" && globalThis.__MM_SW_PATH__ ? globalThis.__MM_SW_PATH__ : "/mm-stats-sw.js",
  disableSw = false
}) {
  const basePath = useMemo2(() => getBasePath(), []);
  const resolvedSwPath = useMemo2(() => resolveSwPath(swPath, basePath), [swPath, basePath]);
  const swScope = useMemo2(() => resolveSwScope(basePath), [basePath]);
  const envCdnPatterns = useMemo2(() => {
    if (typeof import.meta === "undefined") return [];
    const env = import.meta.env;
    if (!env) return [];
    return [env.VITE_MM_CDN_URL || "", env.VITE_API_ENDPOINT || ""].filter(Boolean);
  }, []);
  const cdnPatternKey = useMemo2(() => {
    const sourcePatterns = typeof cdnPattern === "undefined" ? envCdnPatterns : Array.isArray(cdnPattern) ? cdnPattern.filter(Boolean) : cdnPattern ? [cdnPattern] : [];
    const rawPatterns = sourcePatterns;
    const patterns = rawPatterns.map((pattern) => normalizeCdnPattern(pattern)).filter((value) => Boolean(value));
    return JSON.stringify(patterns);
  }, [cdnPattern, envCdnPatterns]);
  const cdnPatternList = useMemo2(() => {
    try {
      return JSON.parse(cdnPatternKey).filter(Boolean);
    } catch {
      return [];
    }
  }, [cdnPatternKey]);
  const [showOverlay, setShowOverlay] = useState2(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("mm_stats_overlay") === "1";
    } catch {
      return false;
    }
  });
  const [stats, setStats] = useState2(/* @__PURE__ */ new Map());
  const aggregates = useMemo2(() => calculateAggregates(stats), [stats]);
  const isMountedRef = useRef2(true);
  const SW_CONTROL_RELOAD_KEY = "mm_stats_sw_reload_once";
  const urlToStatsIdRef = useRef2(/* @__PURE__ */ new Map());
  const pendingStatsByUrlRef = useRef2(/* @__PURE__ */ new Map());
  const cachedStatsByUrlRef = useRef2(/* @__PURE__ */ new Map());
  const cacheLoadedRef = useRef2(false);
  const CACHE_KEY = "mm_stats_cache_v1";
  const CACHE_MAX = 300;
  useEffect2(() => {
    const handleKeyDown = (event) => {
      const isMac = typeof navigator !== "undefined" && (navigator.platform.toUpperCase().indexOf("MAC") >= 0 || navigator.userAgent.toUpperCase().indexOf("MAC") >= 0);
      const cmdKey = isMac ? event.metaKey : event.ctrlKey;
      const isB = event.key === "b" || event.key === "B";
      if (cmdKey && isB && !event.repeat) {
        event.preventDefault();
        setShowOverlay((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  const registerImage = useCallback((id, imageStats) => {
    if (!isMountedRef.current) return;
    setStats((prev) => {
      const next = new Map(prev);
      const existing = next.get(id);
      next.set(id, existing ? { ...existing, ...imageStats } : imageStats);
      return next;
    });
  }, []);
  const unregisterImage = useCallback((id) => {
    if (!isMountedRef.current) return;
    setStats((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const updateImage = useCallback((id, imageStats) => {
    if (!isMountedRef.current) return;
    setStats((prev) => {
      const next = new Map(prev);
      const existing = next.get(id);
      next.set(id, existing ? { ...existing, ...imageStats } : imageStats);
      return next;
    });
  }, []);
  const toggleOverlay = useCallback(() => {
    if (isMountedRef.current) {
      setShowOverlay((prev) => !prev);
    }
  }, []);
  const clearAll = useCallback(() => {
    if (isMountedRef.current) {
      setStats(/* @__PURE__ */ new Map());
      setShowOverlay(false);
    }
  }, []);
  useEffect2(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("mm_stats_overlay", showOverlay ? "1" : "0");
    } catch {
    }
  }, [showOverlay]);
  useEffect2(() => {
    if (!showOverlay) return;
    if (disableSw) return;
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    if (navigator.serviceWorker.controller) return;
    if (typeof window === "undefined") return;
    try {
      const alreadyReloaded = window.sessionStorage.getItem(SW_CONTROL_RELOAD_KEY) === "1";
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(SW_CONTROL_RELOAD_KEY, "1");
        window.location.reload();
      }
    } catch {
    }
  }, [showOverlay, disableSw]);
  const registerImageUrl = useCallback((statsId, url) => {
    const normalized = normalizeStatsUrl(url);
    urlToStatsIdRef.current.set(normalized, statsId);
    const pending = pendingStatsByUrlRef.current.get(normalized);
    const cached = cachedStatsByUrlRef.current.get(normalized);
    if (pending) {
      pendingStatsByUrlRef.current.delete(normalized);
      setStats((prev) => {
        const existing = prev.get(statsId);
        let changed = !existing;
        if (existing) {
          for (const [key, value] of Object.entries(pending)) {
            if (existing[key] !== value) {
              changed = true;
              break;
            }
          }
        }
        if (!changed) return prev;
        const merged = { ...existing || {}, ...pending };
        const next = new Map(prev);
        next.set(statsId, merged);
        return next;
      });
      return;
    }
    if (cached) {
      setStats((prev) => {
        const existing = prev.get(statsId);
        let changed = !existing;
        if (existing) {
          for (const [key, value] of Object.entries(cached)) {
            if (existing[key] !== value) {
              changed = true;
              break;
            }
          }
        }
        if (!changed) return prev;
        const merged = { ...existing || {}, ...cached };
        const next = new Map(prev);
        next.set(statsId, merged);
        return next;
      });
    }
  }, []);
  const unregisterImageUrl = useCallback((url) => {
    const normalized = normalizeStatsUrl(url);
    urlToStatsIdRef.current.delete(normalized);
  }, []);
  const getStatsForUrl = useCallback((url) => {
    const normalized = normalizeStatsUrl(url);
    const statsId = urlToStatsIdRef.current.get(normalized);
    if (!statsId) return void 0;
    return stats.get(statsId);
  }, [stats]);
  useEffect2(() => {
    if (!cacheLoadedRef.current && typeof window !== "undefined") {
      cacheLoadedRef.current = true;
      try {
        const raw = window.sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          const entries = JSON.parse(raw);
          cachedStatsByUrlRef.current = new Map(entries);
        }
      } catch {
      }
    }
    if (disableSw) return;
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const patterns = cdnPatternList;
    if (patterns.length === 0) return;
    let swRegistration;
    const registrationOptions = { scope: swScope, type: "module" };
    navigator.serviceWorker.register(resolvedSwPath, registrationOptions).then((reg) => {
      if (!isMountedRef.current) return;
      swRegistration = reg;
      const sendConfig = (sw) => {
        sw.postMessage({
          type: "mm-stats-config",
          cdnPatterns: patterns
        });
      };
      if (reg.active) {
        sendConfig(reg.active);
      }
      const onStateChange = () => {
        if (reg.active) {
          sendConfig(reg.active);
        }
      };
      if (reg.installing) {
        reg.installing.addEventListener("statechange", onStateChange);
      } else if (reg.waiting) {
        reg.waiting.addEventListener("statechange", onStateChange);
      }
    }).catch((err) => {
      if (process.env.NODE_ENV !== "production") {
        warn("[media-magic] Stats Service Worker registration failed:", err);
      }
    });
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== "mm-stats") return;
      if (!isMountedRef.current) return;
      const normalizedUrl = normalizeStatsUrl(data.url);
      cachedStatsByUrlRef.current.set(normalizedUrl, data.stats);
      if (typeof window !== "undefined") {
        try {
          const entries = Array.from(cachedStatsByUrlRef.current.entries()).slice(-CACHE_MAX);
          window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(entries));
        } catch {
        }
      }
      const statsId = urlToStatsIdRef.current.get(normalizedUrl);
      if (!statsId) {
        pendingStatsByUrlRef.current.set(normalizedUrl, data.stats);
        return;
      }
      setStats((prev) => {
        const existing = prev.get(statsId);
        const merged = { ...existing || {}, ...data.stats };
        const next = new Map(prev);
        next.set(statsId, merged);
        return next;
      });
      if (process.env.NODE_ENV !== "production") {
        const g = globalThis;
        g.__MM_LAST_SW__ = data;
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [cdnPatternKey, cdnPatternList, resolvedSwPath, swScope, disableSw]);
  const contextValue = useMemo2(
    () => ({
      showOverlay,
      stats,
      aggregates,
      registerImage,
      unregisterImage,
      updateImage,
      toggleOverlay,
      setShowOverlay,
      clearAll,
      registerImageUrl,
      unregisterImageUrl,
      getStatsForUrl
    }),
    [showOverlay, stats, aggregates, registerImage, unregisterImage, updateImage, toggleOverlay, clearAll, registerImageUrl, unregisterImageUrl, getStatsForUrl]
  );
  useEffect2(() => {
    if (process.env.NODE_ENV === "production") return;
    const g = globalThis;
    g.__MM_STATS_STATE__ = {
      stats: Array.from(stats.entries()),
      aggregates,
      showOverlay
    };
  }, [stats, aggregates, showOverlay]);
  useEffect2(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const shouldRenderGlobal = (() => {
    if (!showOverlay) return false;
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 820px)").matches === false;
  })();
  return /* @__PURE__ */ jsxs2(StatsContext.Provider, { value: contextValue, children: [
    children,
    shouldRenderGlobal && /* @__PURE__ */ jsx3(GlobalStatsDashboard, {})
  ] });
}

// src/hooks/useStatsOverlay.ts
function useStatsOverlay() {
  return useContext3(StatsContext);
}

// src/components/StatsOverlay.tsx
import { useState as useState3 } from "react";
import { Fragment, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function StatsOverlay({ stats, position = "bottom-right" }) {
  const [isHovered, setIsHovered] = useState3(false);
  const positionStyles = {
    "bottom-right": {
      position: "absolute",
      bottom: "8px",
      right: "8px"
    },
    "bottom-left": {
      position: "absolute",
      bottom: "8px",
      left: "8px"
    },
    "top-right": {
      position: "absolute",
      top: "8px",
      right: "8px"
    },
    "top-left": {
      position: "absolute",
      top: "8px",
      left: "8px"
    }
  };
  const overlayStyle = {
    ...positionStyles[position],
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    color: "#fff",
    padding: isHovered ? "8px 12px" : "6px 10px",
    borderRadius: "4px",
    fontSize: "10px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace',
    lineHeight: "1.4",
    zIndex: 2,
    pointerEvents: "auto",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(4px)",
    transition: "all 0.2s ease"
  };
  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    whiteSpace: "nowrap",
    marginBottom: isHovered ? "4px" : "0px"
  };
  const labelStyle = {
    opacity: 0.8,
    fontWeight: "normal",
    fontSize: isHovered ? "10px" : "10px"
  };
  const valueStyle = {
    fontWeight: 600,
    color: "#4ade80",
    fontSize: isHovered ? "11px" : "10px"
  };
  const primaryBadgeStyle = {
    fontWeight: 600,
    fontSize: "11px",
    color: getReductionColor(stats.sizeReductionPercent ?? 0),
    letterSpacing: "0.01em"
  };
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      style: overlayStyle,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      children: [
        stats.sizeReductionPercent !== void 0 && !isHovered && /* @__PURE__ */ jsx4("div", { style: rowStyle, children: /* @__PURE__ */ jsxs3("span", { style: primaryBadgeStyle, children: [
          "\u26A1 ",
          stats.sizeReductionPercent.toFixed(1),
          "%"
        ] }) }),
        isHovered && /* @__PURE__ */ jsxs3(Fragment, { children: [
          stats.sizeReductionPercent !== void 0 && /* @__PURE__ */ jsxs3("div", { style: rowStyle, children: [
            /* @__PURE__ */ jsx4("span", { style: labelStyle, children: "Size Reduction" }),
            /* @__PURE__ */ jsxs3("span", { style: primaryBadgeStyle, children: [
              stats.sizeReductionPercent.toFixed(1),
              "%"
            ] })
          ] }),
          stats.originalSizeBytes !== void 0 && stats.optimizedSizeBytes !== void 0 && /* @__PURE__ */ jsxs3("div", { style: rowStyle, children: [
            /* @__PURE__ */ jsx4("span", { style: labelStyle, children: "Size" }),
            /* @__PURE__ */ jsxs3("span", { style: valueStyle, children: [
              formatBytes(stats.originalSizeBytes),
              " \u2192 ",
              formatBytes(stats.optimizedSizeBytes)
            ] })
          ] }),
          stats.loadTimeMs !== void 0 && /* @__PURE__ */ jsxs3("div", { style: rowStyle, children: [
            /* @__PURE__ */ jsx4("span", { style: labelStyle, children: "Load" }),
            /* @__PURE__ */ jsxs3("span", { style: { ...valueStyle, color: getLoadTimeColor(stats.loadTimeMs) }, children: [
              stats.loadTimeMs.toFixed(0),
              " ms"
            ] })
          ] }),
          stats.cacheStatus && /* @__PURE__ */ jsxs3("div", { style: rowStyle, children: [
            /* @__PURE__ */ jsx4("span", { style: labelStyle, children: "Cache" }),
            /* @__PURE__ */ jsx4("span", { style: {
              ...valueStyle,
              color: stats.cacheStatus === "hit" ? "#60a5fa" : stats.cacheStatus === "miss" ? "#fb923c" : stats.cacheStatus === "expired" ? "#f87171" : "#9ca3af"
            }, children: stats.cacheStatus })
          ] }),
          stats.cropApplied && /* @__PURE__ */ jsxs3("div", { style: rowStyle, children: [
            /* @__PURE__ */ jsx4("span", { style: labelStyle, children: "Crop" }),
            /* @__PURE__ */ jsx4("span", { style: valueStyle, children: stats.autofocusMode || "yes" })
          ] })
        ] })
      ]
    }
  );
}

// src/utils/urlBuilder.ts
function buildTransformSegments(options) {
  const segments = [];
  if (options.width) {
    segments.push(`w${options.width}`);
  }
  if (options.height) {
    segments.push(`h${options.height}`);
  }
  if (options.resize) {
    segments.push("resize");
  }
  if (options.crop) {
    segments.push("crop");
  }
  if (options.autofocus) {
    if (typeof options.autofocus === "string") {
      segments.push(`autofocus-${options.autofocus}`);
    } else {
      segments.push("autofocus");
    }
  }
  if (options.cropBox) {
    const { left, top, right, bottom } = options.cropBox;
    segments.push(`l${left}`, `t${top}`, `r${right}`, `b${bottom}`);
  }
  if (options.quality && options.quality !== 85) {
    segments.push(`q${options.quality}`);
  }
  return segments;
}
function buildUrl(cdnUrl, clientId, mediaId, options = {}) {
  const segments = buildTransformSegments(options);
  if (segments.length === 0) {
    return `${cdnUrl}/image/${clientId}/${mediaId}.webp`;
  }
  const transformString = segments.join("-");
  return `${cdnUrl}/image/${clientId}/${mediaId}-${transformString}.webp`;
}
function buildSrcSet(cdnUrl, clientId, mediaId, widths, options = {}) {
  return widths.map((width) => {
    const url = buildUrl(cdnUrl, clientId, mediaId, { ...options, width });
    return `${url} ${width}w`;
  }).join(", ");
}
function buildBlurUrl(cdnUrl, clientId, mediaId) {
  return buildUrl(cdnUrl, clientId, mediaId, {
    width: 20,
    quality: 30,
    resize: true
  });
}
function mergeTransformOptions(...optionsList) {
  const result = {};
  for (const options of optionsList) {
    if (!options) continue;
    if (options.width !== void 0) result.width = options.width;
    if (options.height !== void 0) result.height = options.height;
    if (options.resize !== void 0) result.resize = options.resize;
    if (options.crop !== void 0) result.crop = options.crop;
    if (options.autofocus !== void 0) result.autofocus = options.autofocus;
    if (options.cropBox !== void 0) result.cropBox = options.cropBox;
    if (options.quality !== void 0) result.quality = options.quality;
  }
  return result;
}

// src/hooks/useContainerSize.ts
import { useState as useState4, useEffect as useEffect3, useCallback as useCallback2, useRef as useRef3 } from "react";
function useContainerSize(options = {}) {
  const {
    debounce = 300,
    step = 50,
    useDevicePixelRatio = true,
    maxWidth = 2400
  } = options;
  const [size, setSize] = useState4(null);
  const [metrics, setMetrics] = useState4(null);
  const observerRef = useRef3(null);
  const timeoutRef = useRef3(null);
  const currentNodeRef = useRef3(null);
  const hasInitialMeasurement = useRef3(false);
  const optionsRef = useRef3({ debounce, step, useDevicePixelRatio, maxWidth });
  optionsRef.current = { debounce, step, useDevicePixelRatio, maxWidth };
  const roundToStep = useCallback2((value) => {
    const opts = optionsRef.current;
    const dpr = opts.useDevicePixelRatio ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    const physicalWidth = Math.ceil(value * dpr);
    const rounded = opts.step > 1 ? Math.ceil(physicalWidth / opts.step) * opts.step : physicalWidth;
    return Math.min(rounded, opts.maxWidth);
  }, []);
  const getCurrentDpr = useCallback2(() => {
    const opts = optionsRef.current;
    return opts.useDevicePixelRatio ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  }, []);
  const applyMeasurement = useCallback2((cssWidth, cssHeight, observedNode) => {
    const roundedWidth = roundToStep(cssWidth);
    const roundedHeight = roundToStep(cssHeight);
    setSize((prev) => {
      if (prev?.width === roundedWidth && prev?.height === roundedHeight) {
        return prev;
      }
      return { width: roundedWidth, height: roundedHeight };
    });
    setMetrics((prev) => {
      const next = {
        cssWidth,
        cssHeight,
        roundedWidth,
        roundedHeight,
        clientWidth: observedNode?.clientWidth ?? 0,
        clientHeight: observedNode?.clientHeight ?? 0,
        dpr: getCurrentDpr()
      };
      if (prev && prev.cssWidth === next.cssWidth && prev.cssHeight === next.cssHeight && prev.roundedWidth === next.roundedWidth && prev.roundedHeight === next.roundedHeight && prev.clientWidth === next.clientWidth && prev.clientHeight === next.clientHeight && prev.dpr === next.dpr) {
        return prev;
      }
      return next;
    });
  }, [roundToStep, getCurrentDpr]);
  const scheduleMeasurement = useCallback2((cssWidth, cssHeight, observedNode) => {
    const opts = optionsRef.current;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (opts.debounce <= 0) {
      applyMeasurement(cssWidth, cssHeight, observedNode);
      return;
    }
    timeoutRef.current = setTimeout(() => {
      applyMeasurement(cssWidth, cssHeight, currentNodeRef.current ?? observedNode);
      timeoutRef.current = null;
    }, opts.debounce);
  }, [applyMeasurement]);
  const refCallback = useCallback2((node) => {
    if (currentNodeRef.current === node) {
      return;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    currentNodeRef.current = node;
    hasInitialMeasurement.current = false;
    if (!node) {
      setSize(null);
      setMetrics(null);
      return;
    }
    const rect = node.getBoundingClientRect();
    if (rect.width > 0) {
      applyMeasurement(rect.width, rect.height, node);
      hasInitialMeasurement.current = true;
    }
    observerRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width === 0) return;
      if (!hasInitialMeasurement.current) {
        applyMeasurement(width, height, currentNodeRef.current);
        hasInitialMeasurement.current = true;
      } else {
        scheduleMeasurement(width, height, currentNodeRef.current);
      }
    });
    observerRef.current.observe(node);
  }, [applyMeasurement, scheduleMeasurement]);
  useEffect3(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  return [refCallback, size, metrics];
}
function calculateImageDimensions(containerWidth, aspectRatio, containerHeight, preferLargerDimension = false) {
  if (!aspectRatio) {
    return { width: containerWidth, height: containerHeight };
  }
  let ratio;
  if (aspectRatio.includes("/")) {
    const [w, h] = aspectRatio.split("/").map(Number);
    ratio = w / h;
  } else {
    ratio = parseFloat(aspectRatio);
  }
  if (isNaN(ratio) || ratio <= 0) {
    return { width: containerWidth };
  }
  if (preferLargerDimension && typeof containerHeight === "number" && Number.isFinite(containerHeight) && containerHeight > containerWidth) {
    const height2 = Math.round(containerHeight);
    const width = Math.max(1, Math.round(height2 * ratio));
    return { width, height: height2 };
  }
  const height = Math.round(containerWidth / ratio);
  return { width: containerWidth, height };
}

// src/utils/resolutionMap.ts
function lookupMediaId(src) {
  const map = globalThis.__MM_RESOLUTION_MAP__;
  if (!map) return null;
  if (map[src]) return map[src];
  const stripped = src.replace(/^\.\//, "");
  if (map[stripped]) return map[stripped];
  const baseUrl = typeof import.meta !== "undefined" && import.meta.env?.BASE_URL ? import.meta.env.BASE_URL : "/";
  if (baseUrl !== "/" && src.startsWith(baseUrl)) {
    const withoutBase = src.slice(baseUrl.length);
    const normalized = withoutBase.startsWith("/") ? withoutBase : "/" + withoutBase;
    if (map[normalized]) return map[normalized];
  }
  return null;
}

// src/components/MagicImageMin.tsx
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var log = (..._args) => {
};
var warn2 = (..._args) => {
};
var RETRY_DELAYS_MS = [300, 900, 1800, 3200];
var MEDIA_ID_INVALID_WATCHDOG_MS = 8e3;
var REVALIDATE_MEDIA_ID_TIMEOUT_MS = 1e4;
var REVALIDATED_MEDIA_ID_CACHE_MAX = 1e3;
var runtimeInstanceCounter = 0;
var SHOULD_LOG_RUNTIME = process.env.NODE_ENV !== "production";
function shouldLogRuntime() {
  if (!SHOULD_LOG_RUNTIME) return false;
  const flag = globalThis.__MM_VERBOSE_LOGS__;
  return flag !== false;
}
function withRetryParam(url, retryAttempt) {
  if (retryAttempt <= 0) return url;
  return `${url}${url.includes("?") ? "&" : "?"}__mmr=${retryAttempt}`;
}
var revalidatedMediaIdCache = /* @__PURE__ */ new Map();
function cacheRevalidatedMediaId(staleMediaId, freshMediaId) {
  if (!staleMediaId || !freshMediaId || staleMediaId === freshMediaId) return;
  revalidatedMediaIdCache.set(staleMediaId, freshMediaId);
  if (revalidatedMediaIdCache.size > REVALIDATED_MEDIA_ID_CACHE_MAX) {
    const oldestKey = revalidatedMediaIdCache.keys().next().value;
    if (oldestKey) {
      revalidatedMediaIdCache.delete(oldestKey);
    }
  }
}
function normalizeMediaId(value) {
  const mediaId = value?.trim?.();
  if (!mediaId) return null;
  if (mediaId.toLowerCase() === "undefined" || mediaId.toLowerCase() === "null") return null;
  return mediaId;
}
function extractRevalidatedMediaId(payload) {
  if (!payload || typeof payload !== "object") return null;
  const data = payload;
  const direct = normalizeMediaId(typeof data.mediaId === "string" ? data.mediaId : "");
  if (direct) return direct;
  const legacy = normalizeMediaId(typeof data.thumbnailMediaId === "string" ? data.thumbnailMediaId : "");
  if (legacy) return legacy;
  const next = normalizeMediaId(typeof data.newMediaId === "string" ? data.newMediaId : "");
  if (next) return next;
  return null;
}
async function revalidateMediaIdFromEndpoint(path, staleMediaId, resolver, getAuthToken) {
  let headers = {
    "Content-Type": "application/json"
  };
  if (getAuthToken) {
    try {
      const maybeToken = await getAuthToken();
      const token = typeof maybeToken === "string" ? maybeToken.trim() : null;
      if (token) {
        headers = {
          ...headers,
          Authorization: `Bearer ${token}`
        };
      }
    } catch {
    }
  }
  let timeoutRef = null;
  const controller = typeof AbortController !== "undefined" ? new AbortController() : void 0;
  if (controller) {
    timeoutRef = setTimeout(() => controller.abort(), REVALIDATE_MEDIA_ID_TIMEOUT_MS);
  }
  try {
    const response = await fetch(path, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({
        mediaId: staleMediaId,
        ...resolver ? { resolver } : {}
      }),
      signal: controller?.signal
    });
    if (!response.ok) {
      return null;
    }
    const payload = await response.json().catch(() => null);
    return extractRevalidatedMediaId(payload);
  } finally {
    if (timeoutRef) {
      clearTimeout(timeoutRef);
    }
  }
}
function assignForwardedRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref) {
    ref.current = value;
  }
}
function isPositiveNumber(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}
function resolveObjectPosition(position) {
  const positionMap = {
    "center": "center center",
    "top": "center top",
    "bottom": "center bottom",
    "left": "left center",
    "right": "right center",
    "top-left": "left top",
    "top-right": "right top",
    "bottom-left": "left bottom",
    "bottom-right": "right bottom"
  };
  return positionMap[position] || position;
}
function getLayoutStyles(layout, width, height, objectFit, objectPosition, aspectRatio) {
  const baseStyles = {};
  if (objectFit) {
    baseStyles.objectFit = objectFit;
  }
  if (objectPosition) {
    baseStyles.objectPosition = objectPosition;
  }
  if (aspectRatio) {
    baseStyles.aspectRatio = aspectRatio;
  }
  switch (layout) {
    case "intrinsic":
      return {
        ...baseStyles,
        maxWidth: "100%",
        height: "auto",
        ...width && { width },
        ...height && { height }
      };
    case "responsive":
      return {
        ...baseStyles,
        width: "100%",
        height: "auto",
        display: "block"
      };
    case "fill":
      return {
        ...baseStyles,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: objectFit || "cover"
      };
    case "fixed":
      return {
        ...baseStyles,
        width: width || "auto",
        height: height || "auto",
        flexShrink: 0
      };
    default:
      return baseStyles;
  }
}
var InternalImage = forwardRef(function InternalImage2({
  mediaId,
  fallbackSrc,
  onMediaIdInvalid,
  revalidateResolver,
  revalidateAuthTokenProvider,
  clientId: clientIdProp,
  computedWidth,
  computedHeight,
  smartCropPreferAxis,
  width: widthProp,
  height: heightProp,
  crop,
  autofocus,
  quality,
  layout = "intrinsic",
  objectFit,
  objectPosition,
  aspectRatio,
  sizes: responsiveSizes,
  sizesAttr,
  style,
  className,
  loading = "lazy",
  decoding = "async",
  crossOrigin,
  priority,
  placeholder,
  blurDataURL,
  onLoad,
  onError,
  showStats,
  onStatsChange,
  ...rest
}, ref) {
  const config = useMediaMagicConfig();
  const statsContext = useStatsOverlay();
  const normalizedPropMediaId = useMemo3(() => normalizeMediaId(mediaId), [mediaId]);
  const [revalidatedMediaId, setRevalidatedMediaId] = useState5(() => {
    if (!normalizedPropMediaId) return null;
    return revalidatedMediaIdCache.get(normalizedPropMediaId) || null;
  });
  const activeMediaId = revalidatedMediaId || normalizedPropMediaId;
  const hasValidMediaId = Boolean(activeMediaId);
  const [isLoaded, setIsLoaded] = useState5(false);
  const [hasError, setHasError] = useState5(false);
  const [retryAttempt, setRetryAttempt] = useState5(0);
  const [isRecoveringFromError, setIsRecoveringFromError] = useState5(false);
  const [stats, setStats] = useState5(null);
  const retryTimerRef = useRef4(null);
  const imageElementRef = useRef4(null);
  const effectiveSrcRef = useRef4(void 0);
  const invalidNotifiedMediaIdRef = useRef4(null);
  const invalidWatchdogTimerRef = useRef4(null);
  const sdkRevalidatePromiseRef = useRef4(null);
  const isMountedRef = useRef4(true);
  const onMediaIdInvalidRef = useRef4(onMediaIdInvalid);
  const runtimeInstanceIdRef = useRef4(++runtimeInstanceCounter);
  const loadStartTimeRef = useRef4(null);
  const statsIdRef = useRef4(Math.random().toString(36).slice(2, 11));
  const statsContextRef = useRef4(statsContext);
  const clientId = clientIdProp || config.clientId;
  const { cdnUrl, defaultTransforms } = config;
  const revalidateMediaIdPath = (config.revalidateMediaIdPath || "").trim();
  const hasSdkRevalidatePath = Boolean(revalidateMediaIdPath);
  const effectiveRevalidateResolver = (revalidateResolver || config.revalidateResolver || "").trim();
  const effectiveRevalidateAuthProvider = revalidateAuthTokenProvider || config.revalidateAuthTokenProvider || null;
  useEffect4(() => {
    if (!normalizedPropMediaId) {
      setRevalidatedMediaId(null);
    } else {
      setRevalidatedMediaId(revalidatedMediaIdCache.get(normalizedPropMediaId) || null);
    }
    invalidNotifiedMediaIdRef.current = null;
  }, [normalizedPropMediaId]);
  useEffect4(() => {
    onMediaIdInvalidRef.current = onMediaIdInvalid;
    if (shouldLogRuntime()) {
      const payload = {
        instance: runtimeInstanceIdRef.current,
        mediaIdProp: mediaId,
        hasCallback: Boolean(onMediaIdInvalid)
      };
      const g = globalThis;
      g.__MM_RUNTIME_EVENTS__ = g.__MM_RUNTIME_EVENTS__ || [];
      g.__MM_RUNTIME_EVENTS__.push({
        at: Date.now(),
        event: "callback-prop-updated",
        ...payload
      });
      if (g.__MM_RUNTIME_EVENTS__.length > 500) g.__MM_RUNTIME_EVENTS__.shift();
      log("[media-magic] [runtime] callback-prop-updated", payload);
    }
  }, [onMediaIdInvalid]);
  const logRuntime = (event, extra) => {
    if (!shouldLogRuntime()) return;
    const payload = {
      event,
      instance: runtimeInstanceIdRef.current,
      mediaIdProp: mediaId,
      normalizedMediaId: activeMediaId,
      sourceMediaId: normalizedPropMediaId,
      revalidatedMediaId,
      hasValidMediaId,
      hasCallback: Boolean(onMediaIdInvalidRef.current),
      hasSdkRevalidatePath,
      revalidateResolver: effectiveRevalidateResolver || null,
      retryAttempt,
      isLoaded,
      hasError,
      src,
      effectiveSrc,
      ...extra
    };
    const g = globalThis;
    g.__MM_RUNTIME_EVENTS__ = g.__MM_RUNTIME_EVENTS__ || [];
    g.__MM_RUNTIME_EVENTS__.push({
      at: Date.now(),
      ...payload
    });
    if (g.__MM_RUNTIME_EVENTS__.length > 500) g.__MM_RUNTIME_EVENTS__.shift();
    log("[media-magic] [runtime]", payload);
  };
  useEffect4(() => {
    logRuntime("mounted");
    return () => {
      logRuntime("unmounted");
    };
  }, []);
  const hasComputedWidth = isPositiveNumber(computedWidth);
  const hasComputedHeight = isPositiveNumber(computedHeight);
  const hasPropWidth = isPositiveNumber(widthProp);
  const hasPropHeight = isPositiveNumber(heightProp);
  const width = hasComputedWidth ? computedWidth : hasPropWidth ? widthProp : void 0;
  const height = hasComputedHeight ? computedHeight : (() => {
    if (!hasPropHeight) return void 0;
    if (!hasComputedWidth || !hasPropWidth) return heightProp;
    return Math.max(1, Math.round(computedWidth * heightProp / widthProp));
  })();
  const renderWidth = hasPropWidth ? widthProp : width;
  const renderHeight = hasPropHeight ? heightProp : height;
  const isSmartCrop = Boolean(crop) && (autofocus === true || autofocus === "smart");
  const hasAutofocusMode = Boolean(autofocus);
  const useFreeCrop = Boolean(crop) && !hasAutofocusMode && !aspectRatio && !isPositiveNumber(heightProp);
  let transformWidth = width;
  let transformHeight = useFreeCrop ? void 0 : height;
  if (isSmartCrop && hasComputedWidth && hasComputedHeight && !hasPropWidth && !hasPropHeight) {
    const preferHeight = smartCropPreferAxis ? smartCropPreferAxis === "height" : computedHeight > computedWidth;
    if (preferHeight) {
      transformWidth = void 0;
      transformHeight = height;
    } else {
      transformWidth = width;
      transformHeight = void 0;
    }
  }
  const src = useMemo3(() => {
    if (!activeMediaId) {
      return fallbackSrc;
    }
    const options = mergeTransformOptions(defaultTransforms, {
      width: transformWidth,
      height: transformHeight,
      crop,
      autofocus,
      quality,
      resize: !crop && (!!transformWidth || !!transformHeight)
    });
    return buildUrl(cdnUrl, clientId, activeMediaId, options);
  }, [cdnUrl, clientId, activeMediaId, transformWidth, transformHeight, crop, autofocus, quality, defaultTransforms, fallbackSrc]);
  useEffect4(() => {
    logRuntime("src-reset-effect-start");
    setRetryAttempt(0);
    setHasError(false);
    setIsLoaded(false);
    setIsRecoveringFromError(false);
    setStats(null);
    loadStartTimeRef.current = performance.now();
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    logRuntime("src-reset-effect-end");
  }, [src]);
  const srcSet = useMemo3(() => {
    if (!hasValidMediaId || !responsiveSizes || responsiveSizes.length === 0) {
      return void 0;
    }
    if (typeof responsiveSizes[0] === "number") {
      const widths = responsiveSizes;
      return buildSrcSet(cdnUrl, clientId, activeMediaId, widths, {
        height: transformHeight,
        crop,
        autofocus,
        quality,
        resize: !crop
      });
    }
    const breakpoints = responsiveSizes;
    return breakpoints.map((bp) => {
      const url = buildUrl(cdnUrl, clientId, activeMediaId, {
        width: bp.width,
        height: bp.height || transformHeight,
        crop,
        autofocus,
        quality,
        resize: !crop
      });
      return `${url} ${bp.width}w`;
    }).join(", ");
  }, [hasValidMediaId, responsiveSizes, cdnUrl, clientId, activeMediaId, transformHeight, crop, autofocus, quality]);
  const effectiveSrc = useMemo3(
    () => src ? withRetryParam(src, retryAttempt) : void 0,
    [src, retryAttempt]
  );
  useEffect4(() => {
    const prevSrc = effectiveSrcRef.current;
    if (prevSrc && statsContextRef.current) {
      statsContextRef.current.unregisterImageUrl(prevSrc);
    }
    effectiveSrcRef.current = effectiveSrc;
    if (effectiveSrc && statsContextRef.current) {
      statsContextRef.current.registerImageUrl(statsIdRef.current, effectiveSrc);
    }
  }, [effectiveSrc]);
  useEffect4(() => {
    statsContextRef.current = statsContext;
  }, [statsContext]);
  const effectiveSrcSet = retryAttempt === 0 ? srcSet : void 0;
  const effectiveSizes = retryAttempt === 0 ? sizesAttr : void 0;
  const imageRenderKey = effectiveSrc || `mm:${activeMediaId || "none"}:${fallbackSrc || "none"}`;
  const shouldUseStatsCors = showStats && statsContext?.showOverlay;
  const effectiveCrossOrigin = crossOrigin ?? (shouldUseStatsCors ? "anonymous" : void 0);
  const overlayStats = statsContext?.stats.get(statsIdRef.current) ?? stats;
  const wrapperStyle = layout === "fill" ? { position: "absolute", inset: 0, display: "block" } : { position: "relative", display: "inline-block" };
  const setImageRef = useCallback3((node) => {
    imageElementRef.current = node;
    assignForwardedRef(ref, node);
  }, [ref]);
  const blurSrc = useMemo3(() => {
    if (blurDataURL) return blurDataURL;
    if (placeholder === "blur" && activeMediaId) {
      return buildBlurUrl(cdnUrl, clientId, activeMediaId);
    }
    return void 0;
  }, [blurDataURL, placeholder, cdnUrl, clientId, activeMediaId]);
  const computedStyles = useMemo3(() => {
    const layoutStyles = getLayoutStyles(
      layout,
      renderWidth,
      renderHeight,
      objectFit,
      objectPosition ? resolveObjectPosition(objectPosition) : void 0,
      aspectRatio
    );
    return {
      ...layoutStyles,
      ...style
    };
  }, [layout, renderWidth, renderHeight, objectFit, objectPosition, aspectRatio, style]);
  const handleLoad = (e) => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (invalidWatchdogTimerRef.current) {
      clearTimeout(invalidWatchdogTimerRef.current);
      invalidWatchdogTimerRef.current = null;
    }
    setIsLoaded(true);
    setHasError(false);
    setIsRecoveringFromError(false);
    invalidNotifiedMediaIdRef.current = null;
    const target = e.currentTarget;
    logRuntime("img-load", {
      currentSrc: target?.currentSrc,
      naturalWidth: target?.naturalWidth,
      naturalHeight: target?.naturalHeight
    });
    if (showStats || statsContext) {
      const loadEndTime = performance.now();
      const loadTimeMs = loadStartTimeRef.current ? Math.round(loadEndTime - loadStartTimeRef.current) : void 0;
      if (statsContext && target?.currentSrc) {
        const resolvedSrc = target.currentSrc;
        const prevSrc = effectiveSrcRef.current;
        if (prevSrc && prevSrc !== resolvedSrc) {
          statsContext.unregisterImageUrl(prevSrc);
        }
        effectiveSrcRef.current = resolvedSrc;
        statsContext.registerImageUrl(statsIdRef.current, resolvedSrc);
      }
      const imageStats = {
        optimizedWidth: target?.naturalWidth,
        optimizedHeight: target?.naturalHeight,
        loadTimeMs
      };
      setStats(imageStats);
      onStatsChange?.(imageStats);
      statsContext?.registerImage(statsIdRef.current, imageStats);
    }
    onLoad?.(e);
  };
  const maybeBreakOnInvalidMediaId = (reason) => {
    if (process.env.NODE_ENV === "production") return;
    const globalFlag = globalThis.__MM_DEBUG_INVALID_MEDIA_ID__;
    if (globalFlag === false) return;
  };
  const triggerSdkRevalidate = (staleMediaId, reason) => {
    if (!hasSdkRevalidatePath) return;
    const cachedMediaId = revalidatedMediaIdCache.get(staleMediaId);
    if (cachedMediaId && cachedMediaId !== staleMediaId) {
      logRuntime("sdk-revalidate-cache-hit", { staleMediaId, cachedMediaId, reason });
      setRetryAttempt(0);
      setHasError(false);
      setIsLoaded(false);
      setIsRecoveringFromError(true);
      setRevalidatedMediaId(cachedMediaId);
      return;
    }
    if (sdkRevalidatePromiseRef.current) {
      logRuntime("sdk-revalidate-skipped-inflight", { staleMediaId, reason });
      return;
    }
    const revalidatePromise = (async () => {
      logRuntime("sdk-revalidate-start", {
        staleMediaId,
        reason,
        endpoint: revalidateMediaIdPath,
        resolver: effectiveRevalidateResolver || null
      });
      try {
        const nextMediaId = await revalidateMediaIdFromEndpoint(
          revalidateMediaIdPath,
          staleMediaId,
          effectiveRevalidateResolver || void 0,
          effectiveRevalidateAuthProvider
        );
        if (!nextMediaId || nextMediaId === staleMediaId) {
          logRuntime("sdk-revalidate-no-change", { staleMediaId, nextMediaId });
          return;
        }
        cacheRevalidatedMediaId(staleMediaId, nextMediaId);
        if (!isMountedRef.current) {
          logRuntime("sdk-revalidate-skipped-unmounted", { staleMediaId, nextMediaId });
          return;
        }
        invalidNotifiedMediaIdRef.current = null;
        setRetryAttempt(0);
        setHasError(false);
        setIsLoaded(false);
        setIsRecoveringFromError(true);
        setRevalidatedMediaId(nextMediaId);
        logRuntime("sdk-revalidate-success", { staleMediaId, nextMediaId });
      } catch (error) {
        logRuntime("sdk-revalidate-failed", {
          staleMediaId,
          reason,
          error: error instanceof Error ? error.message : String(error)
        });
      } finally {
        sdkRevalidatePromiseRef.current = null;
      }
    })();
    sdkRevalidatePromiseRef.current = revalidatePromise;
  };
  const notifyMediaIdInvalid = (reason) => {
    if (!activeMediaId) {
      logRuntime("notify-invalid-skipped-no-media-id", { reason });
      return;
    }
    if (invalidNotifiedMediaIdRef.current === activeMediaId) {
      logRuntime("notify-invalid-skipped-duplicate", { reason });
      return;
    }
    invalidNotifiedMediaIdRef.current = activeMediaId;
    logRuntime("notify-invalid-callback", { reason });
    maybeBreakOnInvalidMediaId(reason);
    onMediaIdInvalidRef.current?.(activeMediaId);
    triggerSdkRevalidate(activeMediaId, reason);
  };
  const handleError = (e) => {
    if (invalidWatchdogTimerRef.current) {
      clearTimeout(invalidWatchdogTimerRef.current);
      invalidWatchdogTimerRef.current = null;
    }
    const target = e.currentTarget;
    logRuntime("img-error", {
      currentSrc: target?.currentSrc,
      naturalWidth: target?.naturalWidth,
      naturalHeight: target?.naturalHeight
    });
    if (!hasValidMediaId) {
      setHasError(true);
      setIsRecoveringFromError(false);
      onError?.(e);
      return;
    }
    const usingRevalidatedMediaId = Boolean(
      revalidatedMediaId && activeMediaId === revalidatedMediaId && revalidatedMediaId !== normalizedPropMediaId
    );
    if (!usingRevalidatedMediaId) {
      notifyMediaIdInvalid("img-error");
    }
    if ((onMediaIdInvalidRef.current || hasSdkRevalidatePath) && !usingRevalidatedMediaId) {
      logRuntime("img-error-stop-retries-because-revalidation-available");
      setHasError(true);
      setIsRecoveringFromError(false);
      onError?.(e);
      return;
    }
    if (retryAttempt < RETRY_DELAYS_MS.length) {
      setIsRecoveringFromError(true);
      const nextAttempt = retryAttempt + 1;
      const delayMs = RETRY_DELAYS_MS[retryAttempt];
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      retryTimerRef.current = setTimeout(() => {
        logRuntime("retry-timer-fired", { nextAttempt });
        setRetryAttempt(nextAttempt);
      }, delayMs);
      logRuntime("retry-scheduled", { nextAttempt, delayMs });
      return;
    }
    if (!usingRevalidatedMediaId) {
      notifyMediaIdInvalid("img-error");
    }
    setHasError(true);
    setIsRecoveringFromError(false);
    onError?.(e);
  };
  useEffect4(() => {
    const hasInvalidHandler = Boolean(onMediaIdInvalidRef.current || hasSdkRevalidatePath);
    if (!hasValidMediaId || isLoaded || hasError || !hasInvalidHandler) {
      logRuntime("watchdog-not-scheduled", {
        hasValidMediaId,
        isLoaded,
        hasError,
        hasCallback: Boolean(onMediaIdInvalidRef.current),
        hasSdkRevalidatePath
      });
      if (invalidWatchdogTimerRef.current) {
        clearTimeout(invalidWatchdogTimerRef.current);
        invalidWatchdogTimerRef.current = null;
        logRuntime("watchdog-cleared-in-guard");
      }
      return;
    }
    const imageEl = imageElementRef.current;
    if (imageEl?.complete && imageEl.naturalWidth > 0) {
      logRuntime("watchdog-skipped-img-already-complete", {
        currentSrc: imageEl.currentSrc
      });
      setIsLoaded(true);
      setHasError(false);
      return;
    }
    if (invalidWatchdogTimerRef.current) {
      clearTimeout(invalidWatchdogTimerRef.current);
      logRuntime("watchdog-cleared-before-reschedule");
    }
    const scheduledSrc = effectiveSrc;
    invalidWatchdogTimerRef.current = setTimeout(() => {
      const currentEl = imageElementRef.current;
      if (currentEl?.complete && currentEl.naturalWidth > 0) {
        invalidWatchdogTimerRef.current = null;
        logRuntime("watchdog-cancelled-img-complete-before-timeout", {
          currentSrc: currentEl.currentSrc
        });
        return;
      }
      if (scheduledSrc !== effectiveSrcRef.current) {
        invalidWatchdogTimerRef.current = null;
        logRuntime("watchdog-cancelled-stale-src", {
          scheduledSrc,
          activeSrc: effectiveSrcRef.current
        });
        return;
      }
      notifyMediaIdInvalid("watchdog-timeout");
      invalidWatchdogTimerRef.current = null;
      logRuntime("watchdog-fired");
    }, MEDIA_ID_INVALID_WATCHDOG_MS);
    logRuntime("watchdog-scheduled", {
      timeoutMs: MEDIA_ID_INVALID_WATCHDOG_MS,
      scheduledSrc
    });
    return () => {
      if (invalidWatchdogTimerRef.current) {
        clearTimeout(invalidWatchdogTimerRef.current);
        invalidWatchdogTimerRef.current = null;
        logRuntime("watchdog-cleared-on-effect-cleanup");
      }
    };
  }, [hasValidMediaId, isLoaded, hasError, hasSdkRevalidatePath, effectiveSrc]);
  useEffect4(() => {
    if (process.env.NODE_ENV !== "production" && !hasValidMediaId && typeof fallbackSrc === "undefined") {
      warn2("[media-magic] MagicImageMin received invalid mediaId without fallbackSrc", mediaId);
    }
  }, [hasValidMediaId, fallbackSrc, mediaId]);
  useEffect4(() => {
    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      if (invalidWatchdogTimerRef.current) {
        clearTimeout(invalidWatchdogTimerRef.current);
      }
      const activeStatsContext = statsContextRef.current;
      activeStatsContext?.unregisterImage(statsIdRef.current);
      if (effectiveSrcRef.current && activeStatsContext) {
        activeStatsContext.unregisterImageUrl(effectiveSrcRef.current);
      }
    };
  }, []);
  if (placeholder === "blur" && blurSrc && !isLoaded && !hasError) {
    return /* @__PURE__ */ jsxs4(
      "span",
      {
        style: {
          ...wrapperStyle,
          width: layout === "fill" ? "100%" : void 0,
          height: layout === "fill" ? "100%" : void 0,
          overflow: "hidden"
        },
        children: [
          /* @__PURE__ */ jsx5(
            "img",
            {
              src: blurSrc,
              alt: "",
              "aria-hidden": "true",
              style: {
                ...computedStyles,
                filter: "blur(20px)",
                transform: "scale(1.1)"
              }
            }
          ),
          /* @__PURE__ */ jsx5(
            "img",
            {
              ref: setImageRef,
              src: effectiveSrc,
              srcSet: effectiveSrcSet,
              sizes: effectiveSizes,
              loading: priority ? "eager" : loading,
              decoding,
              crossOrigin: effectiveCrossOrigin,
              className,
              style: {
                ...computedStyles,
                position: "absolute",
                top: 0,
                left: 0,
                opacity: isLoaded ? 1 : 0,
                transition: "opacity 0.3s ease-in-out"
              },
              onLoad: handleLoad,
              onError: handleError,
              ...rest
            },
            imageRenderKey
          ),
          showStats && statsContext?.showOverlay && overlayStats && /* @__PURE__ */ jsx5(StatsOverlay, { stats: overlayStats, position: "bottom-right" })
        ]
      }
    );
  }
  if (placeholder && typeof placeholder !== "string" && !isLoaded && !hasError) {
    return /* @__PURE__ */ jsxs4(
      "span",
      {
        style: {
          ...wrapperStyle,
          width: layout === "fill" ? "100%" : void 0,
          height: layout === "fill" ? "100%" : void 0
        },
        children: [
          placeholder,
          /* @__PURE__ */ jsx5(
            "img",
            {
              ref: setImageRef,
              src: effectiveSrc,
              srcSet: effectiveSrcSet,
              sizes: effectiveSizes,
              loading: priority ? "eager" : loading,
              decoding,
              crossOrigin: effectiveCrossOrigin,
              className,
              style: {
                ...computedStyles,
                position: "absolute",
                top: 0,
                left: 0,
                opacity: isLoaded ? 1 : 0
              },
              onLoad: handleLoad,
              onError: handleError,
              ...rest
            },
            imageRenderKey
          ),
          showStats && statsContext?.showOverlay && overlayStats && /* @__PURE__ */ jsx5(StatsOverlay, { stats: overlayStats, position: "bottom-right" })
        ]
      }
    );
  }
  const hideActiveImage = !isLoaded && !hasError;
  return /* @__PURE__ */ jsxs4("span", { style: wrapperStyle, children: [
    /* @__PURE__ */ jsx5(
      "img",
      {
        ref: setImageRef,
        src: effectiveSrc,
        srcSet: effectiveSrcSet,
        sizes: effectiveSizes,
        loading: priority ? "eager" : loading,
        decoding,
        crossOrigin: effectiveCrossOrigin,
        className,
        style: {
          ...computedStyles,
          visibility: hideActiveImage ? "hidden" : computedStyles.visibility
        },
        onLoad: handleLoad,
        onError: handleError,
        ...rest
      },
      imageRenderKey
    ),
    showStats && statsContext?.showOverlay && overlayStats && /* @__PURE__ */ jsx5(StatsOverlay, { stats: overlayStats, position: "bottom-right" })
  ] });
});
var MagicImageMin = forwardRef(function MagicImageMin2({ autoSize = true, autoSizeOptions, aspectRatio, src, mediaId: mediaIdProp, ...props }, ref) {
  const mediaId = src ? lookupMediaId(src) ?? src : mediaIdProp;
  if (!autoSize) {
    return /* @__PURE__ */ jsx5(InternalImage, { ref, aspectRatio, mediaId, ...props });
  }
  return /* @__PURE__ */ jsx5(
    AutoSizeImage,
    {
      ref,
      autoSizeOptions,
      aspectRatio,
      mediaId,
      ...props
    }
  );
});
var AutoSizeImage = forwardRef(function AutoSizeImage2({ autoSizeOptions, aspectRatio, layout = "responsive", style, className, ...props }, ref) {
  const step = autoSizeOptions?.step ?? 50;
  const maxWidth = autoSizeOptions?.maxWidth ?? 2400;
  const useDevicePixelRatio = autoSizeOptions?.useDevicePixelRatio ?? true;
  const debounce = autoSizeOptions?.debounce ?? 300;
  const hookOptions = useMemo3(
    () => ({ step, maxWidth, useDevicePixelRatio, debounce }),
    [step, maxWidth, useDevicePixelRatio, debounce]
  );
  const [containerRef, containerSize, containerMetrics] = useContainerSize(hookOptions);
  const isLandscapeAutofocus = Boolean(props.crop) && props.autofocus === "landscape";
  const { computedWidth, computedHeight } = useMemo3(() => {
    if (!containerSize) {
      return { computedWidth: void 0, computedHeight: void 0 };
    }
    let sourceWidth = containerSize.width;
    let sourceHeight = containerSize.height;
    if (isLandscapeAutofocus && containerMetrics) {
      const preciseWidth = Math.max(1, Math.ceil(containerMetrics.cssWidth * containerMetrics.dpr));
      const preciseHeight = Math.max(1, Math.ceil(containerMetrics.cssHeight * containerMetrics.dpr));
      if (preciseWidth > maxWidth) {
        const scale = maxWidth / preciseWidth;
        sourceWidth = maxWidth;
        sourceHeight = Math.max(1, Math.round(preciseHeight * scale));
      } else {
        sourceWidth = preciseWidth;
        sourceHeight = preciseHeight;
      }
    }
    const useSmartCropSizing = Boolean(props.crop) && (props.autofocus === true || props.autofocus === "smart");
    const dims = calculateImageDimensions(
      sourceWidth,
      aspectRatio,
      sourceHeight,
      useSmartCropSizing
    );
    return {
      computedWidth: dims.width,
      computedHeight: dims.height
    };
  }, [containerSize, containerMetrics, isLandscapeAutofocus, maxWidth, props.crop, props.autofocus, aspectRatio]);
  const hasRequestedDimensions = isPositiveNumber(computedWidth) || isPositiveNumber(computedHeight);
  const isSmartCrop = Boolean(props.crop) && (props.autofocus === true || props.autofocus === "smart");
  const smartCropPreferAxis = isSmartCrop && containerMetrics && containerMetrics.cssHeight > 0 && containerMetrics.cssWidth > 0 ? containerMetrics.cssHeight > containerMetrics.cssWidth ? "height" : "width" : void 0;
  const containerStyles = useMemo3(() => {
    const baseStyles = {
      width: "100%",
      display: "block"
    };
    if (layout === "fill") {
      return {
        ...baseStyles,
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%"
      };
    }
    if (aspectRatio) {
      baseStyles.aspectRatio = aspectRatio;
    }
    return baseStyles;
  }, [layout, aspectRatio]);
  return /* @__PURE__ */ jsx5("div", { ref: containerRef, style: containerStyles, className, children: containerSize && hasRequestedDimensions ? /* @__PURE__ */ jsx5(
    InternalImage,
    {
      ref,
      ...props,
      computedWidth,
      computedHeight,
      smartCropPreferAxis,
      aspectRatio,
      layout,
      style
    }
  ) : /* @__PURE__ */ jsx5(
    "div",
    {
      style: {
        width: "100%",
        aspectRatio,
        backgroundColor: "transparent"
      }
    }
  ) });
});

// src/context/DevProvider.tsx
import { createContext as createContext3, useContext as useContext4, useMemo as useMemo4 } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var DevContext = createContext3(null);
function DevProvider({ config, children }) {
  const devValue = useMemo4(
    () => ({
      apiUrl: config.apiUrl,
      resolveEndpoint: config.resolveEndpoint
    }),
    [config.apiUrl, config.resolveEndpoint]
  );
  return /* @__PURE__ */ jsx6(DevContext.Provider, { value: devValue, children: /* @__PURE__ */ jsx6(MediaMagicProvider, { config: config.sdkConfig, children }) });
}
function useDevConfig() {
  const ctx = useContext4(DevContext);
  if (!ctx) {
    throw new Error("useDevConfig must be used within a <DevProvider>.");
  }
  return ctx;
}
function useOptionalDevConfig() {
  return useContext4(DevContext);
}

// src/hooks/useMediaMagicDev.ts
import { useState as useState6, useCallback as useCallback4, useRef as useRef5 } from "react";
var info = (..._args) => {
};
var sharedInflightByPath = /* @__PURE__ */ new Map();
var sharedReadyByMediaId = /* @__PURE__ */ new Set();
var sharedReadyInflightByMediaId = /* @__PURE__ */ new Map();
var WAIT_READY_PER_REQUEST_SECONDS = 5;
var WAIT_READY_INTERVAL_SECONDS = 1;
var WAIT_READY_MAX_ATTEMPTS = 6;
var WAIT_READY_RETRY_DELAY_MS = 250;
function toBrowserFileUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.replace(/\\/g, "/");
  if (normalized.startsWith("@/")) {
    return `/src/${normalized.slice(2)}`;
  }
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
}
function stripBasePrefix(path) {
  const base = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env?.VITE_BASE : void 0;
  const rawBase = typeof base === "string" ? base.trim() : "";
  if (!rawBase || rawBase === "/") return path;
  const normalizedBase = rawBase.endsWith("/") ? rawBase : `${rawBase}/`;
  if (path === normalizedBase.slice(0, -1)) return "/";
  if (path.startsWith(normalizedBase)) {
    return `/${path.slice(normalizedBase.length)}`;
  }
  return path;
}
function invalidateMediaId(mediaId) {
  const normalized = mediaId.trim();
  if (!normalized) return;
  sharedReadyByMediaId.delete(normalized);
  sharedReadyInflightByMediaId.delete(normalized);
}
function clearDevCaches() {
  sharedInflightByPath.clear();
  sharedReadyByMediaId.clear();
  sharedReadyInflightByMediaId.clear();
}
async function sha256Hex(buffer) {
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function waitForReady(endpoint, headers, mediaId) {
  let waitedMs = 0;
  for (let attempt = 1; attempt <= WAIT_READY_MAX_ATTEMPTS; attempt += 1) {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "waitReady",
        mediaId,
        maxWaitSeconds: WAIT_READY_PER_REQUEST_SECONDS,
        intervalSeconds: WAIT_READY_INTERVAL_SECONDS
      })
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody?.error?.message || `Readiness check failed (${response.status})`
      );
    }
    const data = await response.json();
    waitedMs += typeof data.waitedMs === "number" ? data.waitedMs : 0;
    if (data.ready) return;
    if (attempt < WAIT_READY_MAX_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, WAIT_READY_RETRY_DELAY_MS));
    }
  }
  throw new Error(
    `[media-magic] Image preprocessing is still running after ${Math.round(
      waitedMs / 1e3
    )}s. Please retry in a moment.`
  );
}
async function ensureMediaReady(endpoint, headers, mediaId) {
  if (sharedReadyByMediaId.has(mediaId)) return;
  const inflight = sharedReadyInflightByMediaId.get(mediaId);
  if (inflight) {
    await inflight;
    return;
  }
  const waitPromise = waitForReady(endpoint, headers, mediaId).then(() => {
    sharedReadyByMediaId.add(mediaId);
  }).finally(() => {
    sharedReadyInflightByMediaId.delete(mediaId);
  });
  sharedReadyInflightByMediaId.set(mediaId, waitPromise);
  await waitPromise;
}
function useMediaMagicDev() {
  const { apiUrl, resolveEndpoint } = useDevConfig();
  const [isResolving, setIsResolving] = useState6(false);
  const [error, setError] = useState6(null);
  const inflightRef = useRef5(sharedInflightByPath);
  const resolveImage = useCallback4(
    async (path) => {
      const existing = inflightRef.current.get(path);
      if (existing) return existing;
      const promise = (async () => {
        setIsResolving(true);
        setError(null);
        try {
          const fileUrl = toBrowserFileUrl(path);
          const fileResponse = await fetch(fileUrl);
          if (!fileResponse.ok) {
            throw new Error(`Failed to fetch file at "${fileUrl}" (${fileResponse.status})`);
          }
          const fileBuffer = await fileResponse.arrayBuffer();
          const contentHash = await sha256Hex(fileBuffer);
          const endpoint = resolveEndpoint || (apiUrl ? `${apiUrl}/dev/images/resolve` : "/__media_magic/resolve");
          if (!endpoint) {
            throw new Error(
              "Missing resolve endpoint. Provide DevProvider config.resolveEndpoint or config.apiUrl."
            );
          }
          const headers = {
            "Content-Type": "application/json"
          };
          const envKey = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env?.VITE_MM_API_KEY : void 0;
          const key = typeof envKey === "string" ? envKey.trim() : "";
          if (key) {
            headers.Authorization = `Bearer ${key}`;
          }
          const resolvePath = stripBasePrefix(path);
          const resolveResponse = await fetch(endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify({ path: resolvePath, imageHash: contentHash })
          });
          if (!resolveResponse.ok) {
            const errBody = await resolveResponse.json().catch(() => ({}));
            throw new Error(
              errBody?.error?.message || `Resolve failed (${resolveResponse.status})`
            );
          }
          const data = await resolveResponse.json();
          const { mediaId, uploadUrl, uploadKey, cached, status, reasonCode } = data;
          const uploadOwner = uploadKey?.split("/")?.[1];
          if (status === "FAILED") {
            throw new Error(`[media-magic] Resolve failed for "${path}" (${reasonCode || "FAILED"})`);
          }
          if (!mediaId) {
            throw new Error(`[media-magic] Resolve returned no mediaId for "${path}"`);
          }
          if (cached || status === "READY" || status === "PENDING") {
            info(`[media-magic] Reused mediaId ${mediaId} for "${path}" (shared cache)`);
          } else if ((status === "UPLOAD_REQUIRED" || !status && uploadUrl) && uploadUrl) {
            info(
              `[media-magic] Created mediaId ${mediaId} for "${path}"` + (uploadOwner ? ` (owner: ${uploadOwner})` : "")
            );
          } else {
            info(`[media-magic] Reused mediaId ${mediaId} for "${path}"`);
          }
          if ((status === "UPLOAD_REQUIRED" || !status) && uploadUrl) {
            const uploadResponse = await fetch(uploadUrl, {
              method: "PUT",
              body: fileBuffer
            });
            if (!uploadResponse.ok) {
              throw new Error(`Upload to S3 failed (${uploadResponse.status})`);
            }
          }
          await ensureMediaReady(endpoint, headers, mediaId);
          return { mediaId, cached: Boolean(cached), status, reasonCode };
        } catch (err) {
          const resolveError = err instanceof Error ? err : new Error(String(err));
          setError(resolveError);
          throw resolveError;
        } finally {
          setIsResolving(false);
          inflightRef.current.delete(path);
        }
      })();
      inflightRef.current.set(path, promise);
      return promise;
    },
    [apiUrl, resolveEndpoint]
  );
  return { resolveImage, isResolving, error };
}

// src/components/MagicImage.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var warn3 = (..._args) => {
};
function isFilePath(src) {
  return /[/\\]/.test(src) || /\.(jpe?g|png|gif|webp|avif|svg|bmp|tiff?)$/i.test(src);
}
function toDevPreviewUrl(src) {
  if (/^https?:\/\//i.test(src)) return src;
  const normalized = src.replace(/\\/g, "/");
  if (normalized.startsWith("@/")) {
    return `/src/${normalized.slice(2)}`;
  }
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
}
function resolveObjectPosition2(position) {
  const positionMap = {
    center: "center center",
    top: "center top",
    bottom: "center bottom",
    left: "left center",
    right: "right center",
    "top-left": "left top",
    "top-right": "right top",
    "bottom-left": "left bottom",
    "bottom-right": "right bottom"
  };
  return positionMap[position] || position;
}
function getFallbackLayoutStyles(props) {
  const layout = props.layout || "intrinsic";
  const width = typeof props.width === "number" || typeof props.width === "string" ? props.width : void 0;
  const height = typeof props.height === "number" || typeof props.height === "string" ? props.height : void 0;
  const objectFit = props.objectFit || (props.crop ? "cover" : void 0);
  const objectPosition = props.objectPosition ? resolveObjectPosition2(props.objectPosition) : void 0;
  const base = {
    ...objectFit ? { objectFit } : null,
    ...objectPosition ? { objectPosition } : null,
    ...props.aspectRatio ? { aspectRatio: props.aspectRatio } : null
  };
  switch (layout) {
    case "responsive":
      return {
        ...base,
        width: "100%",
        height: "auto",
        display: "block"
      };
    case "fill":
      return {
        ...base,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%"
      };
    case "fixed":
      return {
        ...base,
        width: width || "auto",
        height: height || "auto",
        flexShrink: 0
      };
    case "intrinsic":
    default:
      return {
        ...base,
        maxWidth: "100%",
        height: "auto",
        ...width ? { width } : null,
        ...height ? { height } : null
      };
  }
}
function isUnderAssetDir(src, assets) {
  const normalized = src.replace(/\\/g, "/");
  return assets.some((dir) => {
    const normalizedDir = dir.replace(/\\/g, "/").replace(/^\.\//, "");
    return normalized.startsWith(normalizedDir) || normalized.startsWith("./" + normalizedDir) || normalized.startsWith("/" + normalizedDir);
  });
}
var MagicImage = forwardRef2(function MagicImage2({ src, ...props }, ref) {
  const devConfig = useOptionalDevConfig();
  const config = useOptionalMediaMagicConfig();
  const assets = config?.assets;
  if (isFilePath(src) && devConfig) {
    if (assets && assets.length > 0 && !isUnderAssetDir(src, assets)) {
      return /* @__PURE__ */ jsx7(MagicImageMin, { ref, mediaId: src, ...props });
    }
    return /* @__PURE__ */ jsx7(ResolvedImage, { ref, src, ...props });
  }
  if (isFilePath(src)) {
    return /* @__PURE__ */ jsx7(MagicImageMin, { ref, src, ...props });
  }
  return /* @__PURE__ */ jsx7(MagicImageMin, { ref, mediaId: src, ...props });
});
var ResolvedImage = forwardRef2(
  function ResolvedImage2({ src, ...props }, ref) {
    const { resolveImage } = useMediaMagicDev();
    const [mediaId, setMediaId] = useState7(null);
    const [error, setError] = useState7(null);
    const [status, setStatus] = useState7("resolving");
    const [previewLoaded, setPreviewLoaded] = useState7(false);
    const [previewFailed, setPreviewFailed] = useState7(false);
    const previewSrc = useMemo5(() => toDevPreviewUrl(src), [src]);
    const previewStyles = useMemo5(() => getFallbackLayoutStyles(props), [props]);
    useEffect5(() => {
      let cancelled = false;
      setMediaId(null);
      setError(null);
      setStatus("resolving");
      setPreviewLoaded(false);
      setPreviewFailed(false);
      resolveImage(src).then((result) => {
        if (!cancelled) {
          setMediaId(result.mediaId);
          setStatus("done");
        }
      }).catch((err) => {
        if (!cancelled) {
          setError(err);
          setStatus("error");
        }
      });
      return () => {
        cancelled = true;
      };
    }, [src, resolveImage]);
    if (status === "error" && error) {
      warn3(`[media-magic] Falling back to local source for "${src}": ${error.message}`);
    }
    if (!mediaId || status !== "done") {
      return /* @__PURE__ */ jsx7(
        "img",
        {
          ref,
          src: previewSrc,
          alt: props.alt,
          width: props.width,
          height: props.height,
          className: props.className,
          style: {
            ...previewStyles,
            ...props.style,
            visibility: !previewLoaded || previewFailed ? "hidden" : props.style?.visibility
          },
          loading: props.loading,
          decoding: props.decoding,
          crossOrigin: props.crossOrigin,
          referrerPolicy: props.referrerPolicy,
          title: status === "error" && error ? `[media-magic] ${error.message}` : props.title,
          onLoad: () => {
            setPreviewLoaded(true);
          },
          onError: () => {
            setPreviewFailed(true);
          }
        }
      );
    }
    return /* @__PURE__ */ jsx7(MagicImageMin, { ref, mediaId, ...props, clientId: props.clientId });
  }
);

// src/components/Picture.tsx
import { forwardRef as forwardRef3, useMemo as useMemo6 } from "react";
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
var Picture = forwardRef3(function Picture2({
  mediaId,
  clientId: clientIdProp,
  sources,
  width,
  height,
  crop,
  autofocus,
  quality,
  ...imageProps
}, ref) {
  const config = useMediaMagicConfig();
  const clientId = clientIdProp || config.clientId;
  const { cdnUrl, defaultTransforms } = config;
  const sourceElements = useMemo6(() => {
    return sources.map((source, index) => {
      const options = mergeTransformOptions(defaultTransforms, {
        width: source.width,
        height: source.height || (typeof height === "number" ? height : void 0),
        crop: source.crop ?? crop,
        autofocus: source.autofocus ?? autofocus,
        quality,
        resize: !(source.crop ?? crop) && (!!source.width || !!source.height)
      });
      const srcUrl = buildUrl(cdnUrl, clientId, mediaId, options);
      return /* @__PURE__ */ jsx8(
        "source",
        {
          media: source.media,
          srcSet: srcUrl,
          type: "image/webp"
        },
        `${source.media}-${index}`
      );
    });
  }, [sources, cdnUrl, clientId, mediaId, height, crop, autofocus, quality, defaultTransforms]);
  return /* @__PURE__ */ jsxs5("picture", { children: [
    sourceElements,
    /* @__PURE__ */ jsx8(
      MagicImageMin,
      {
        ref,
        mediaId,
        clientId,
        width,
        height,
        crop,
        autofocus,
        quality,
        ...imageProps
      }
    )
  ] });
});

// src/hooks/useMediaMagic.ts
import { useCallback as useCallback5, useMemo as useMemo7 } from "react";
function useMediaMagic() {
  const config = useMediaMagicConfig();
  const getUrl = useCallback5(
    (mediaId, options) => {
      const mergedOptions = mergeTransformOptions(config.defaultTransforms, options);
      return buildUrl(config.cdnUrl, config.clientId, mediaId, mergedOptions);
    },
    [config.cdnUrl, config.clientId, config.defaultTransforms]
  );
  const getSrcSet = useCallback5(
    (mediaId, widths, options) => {
      const mergedOptions = mergeTransformOptions(config.defaultTransforms, options);
      return buildSrcSet(config.cdnUrl, config.clientId, mediaId, widths, mergedOptions);
    },
    [config.cdnUrl, config.clientId, config.defaultTransforms]
  );
  return useMemo7(
    () => ({
      getUrl,
      getSrcSet,
      clientId: config.clientId,
      cdnUrl: config.cdnUrl
    }),
    [getUrl, getSrcSet, config.clientId, config.cdnUrl]
  );
}

// src/utils/parseStatsHeaders.ts
var HEADER_MAP = [
  { header: "x-mm-original-width", field: "originalWidth", parse: Number },
  { header: "x-mm-original-height", field: "originalHeight", parse: Number },
  { header: "x-mm-original-size-bytes", field: "originalSizeBytes", parse: Number },
  { header: "x-mm-transformed-width", field: "optimizedWidth", parse: Number },
  { header: "x-mm-transformed-height", field: "optimizedHeight", parse: Number },
  { header: "x-mm-transformed-size-bytes", field: "optimizedSizeBytes", parse: Number },
  { header: "x-mm-size-reduction-percent", field: "sizeReductionPercent", parse: parseFloat },
  { header: "x-mm-transform-time-ms", field: "transformTimeMs", parse: Number },
  { header: "x-mm-original-format", field: "originalFormat", parse: String },
  { header: "x-mm-transformed-format", field: "optimizedFormat", parse: String },
  { header: "x-mm-size-saved-bytes", field: "savedBytes", parse: Number },
  {
    header: "x-mm-crop-applied",
    field: "cropApplied",
    parse: (v) => v.toLowerCase() === "true"
  },
  { header: "x-mm-autofocus-mode", field: "autofocusMode", parse: String },
  { header: "x-mm-quality-level", field: "qualityLevel", parse: Number },
  // Note: x-mm-cache-status is intentionally NOT parsed here.
  // We only use CloudFront's x-cache header for cache status instead (see below).
  // Lambda always returns "miss" since it only runs on cache misses.
  { header: "x-mm-request-id", field: "requestId", parse: String }
];
function mapCloudFrontCacheStatus(xCacheValue) {
  if (!xCacheValue) return null;
  const lower = xCacheValue.toLowerCase();
  if (lower.includes("hit")) return "hit";
  if (lower.includes("miss")) return "miss";
  if (lower.includes("error")) return "expired";
  return null;
}
function parseStatsHeaders(headers) {
  let found = false;
  const stats = {};
  for (const { header, field, parse } of HEADER_MAP) {
    const value = headers.get(header);
    if (value === null || value === "") continue;
    const parsed = parse(value);
    if (typeof parsed === "number" && isNaN(parsed)) continue;
    stats[field] = parsed;
    found = true;
  }
  const xCacheValue = headers.get("x-cache");
  if (xCacheValue) {
    const cfStatus = mapCloudFrontCacheStatus(xCacheValue);
    if (cfStatus) {
      stats.cacheStatus = cfStatus;
    }
  }
  return found ? stats : null;
}
export {
  DevProvider,
  GlobalStatsDashboard,
  MagicImage,
  MagicImageMin,
  MediaMagicProvider,
  Picture,
  StatsOverlay,
  StatsProvider,
  buildBlurUrl,
  buildSrcSet,
  buildTransformSegments,
  buildUrl,
  calculateAggregates,
  calculateImageDimensions,
  calculateReductionPercent,
  clearDevCaches,
  formatBytes,
  getDefaultKeyBinding,
  invalidateMediaId,
  mergeTransformOptions,
  parseStatsHeaders,
  useContainerSize,
  useDevConfig,
  useMediaMagic,
  useMediaMagicConfig,
  useMediaMagicDev,
  useOptionalMediaMagicConfig,
  useStatsOverlay
};
